import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  ButtonGroup,
  CircularProgress,
  Alert,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CancelIcon from '@mui/icons-material/Cancel';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface DashboardStats {
  total_facturado: number;
  total_sin_igv: number;
  total_igv: number;
  total_anulado: number;
  total_anulado_sin_igv: number;
  cantidad_pedidos: number;
  cantidad_anulados: number;
  clientes_atendidos: number;
  ticket_promedio: number;
}

type FilterType = 'today' | 'week' | 'month';

const Dashboard: React.FC = () => {
  const { token, userName } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterType>('today');

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_URL}/odoo/dashboard/stats`, {
        params: { filter },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setStats(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const getFilterLabel = () => {
    switch (filter) {
      case 'today':
        return 'Hoy';
      case 'week':
        return 'Esta Semana';
      case 'month':
        return 'Este Mes';
      default:
        return '';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Dashboard de Ventas
            </Typography>
            {userName && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {userName}
              </Typography>
            )}
          </Box>
          <ButtonGroup variant="contained" size="small">
            <Button
              onClick={() => setFilter('today')}
              variant={filter === 'today' ? 'contained' : 'outlined'}
            >
              Hoy
            </Button>
            <Button
              onClick={() => setFilter('week')}
              variant={filter === 'week' ? 'contained' : 'outlined'}
            >
              Semana
            </Button>
            <Button
              onClick={() => setFilter('month')}
              variant={filter === 'month' ? 'contained' : 'outlined'}
            >
              Mes
            </Button>
          </ButtonGroup>
        </Box>

        <Typography variant="subtitle1" sx={{ mb: 3, color: 'text.secondary' }}>
          Mostrando datos de: {getFilterLabel()}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={50} />
          </Box>
        ) : stats ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
            {/* Total Facturado */}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  bgcolor: '#1976d2',
                  color: 'white',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                      Total Facturado
                    </Typography>
                    <TrendingUpIcon fontSize="large" />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem' }, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                    S/ {stats.total_facturado.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    Con IGV incluido
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Valor Venta */}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  bgcolor: '#2e7d32',
                  color: 'white',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                      Valor Venta
                    </Typography>
                    <AttachMoneyIcon fontSize="large" />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem' }, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                    S/ {stats.total_sin_igv.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    Sin IGV (18%)
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Total Anulado */}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  bgcolor: '#d32f2f',
                  color: 'white',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                      Total Anulado
                    </Typography>
                    <MoneyOffIcon fontSize="large" />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem' }, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                    S/ {stats.total_anulado.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    {stats.cantidad_anulados} cancelaciones
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Cobertura */}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  bgcolor: '#ed6c02',
                  color: 'white',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                      Cobertura
                    </Typography>
                    <PeopleIcon fontSize="large" />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem' }, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                    {stats.clientes_atendidos}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    Clientes únicos en periodo
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Cantidad de Pedidos */}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  bgcolor: '#9c27b0',
                  color: 'white',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                      Pedidos
                    </Typography>
                    <ShoppingCartIcon fontSize="large" />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem' }, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                    {stats.cantidad_pedidos}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    Ventas confirmadas
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Ingreso Neto */}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  bgcolor: '#0288d1',
                  color: 'white',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                      Ingreso Neto
                    </Typography>
                    <ReceiptIcon fontSize="large" />
                  </Box>

                  {/* Línea 1: Cálculo */}
                  <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.9, fontSize: { xs: '0.85rem', sm: '0.95rem' }, mb: 2 }}>
                    S/ {stats.total_facturado.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - S/ {stats.total_anulado.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = {(stats.total_facturado - stats.total_anulado).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>

                  {/* Resultado sin IGV */}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem' }, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                      S/ {(stats.total_sin_igv - stats.total_anulado_sin_igv).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.8, fontSize: '0.75rem' }}>
                      Sin IGV
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        ) : (
          <Alert severity="info">No hay datos disponibles para mostrar</Alert>
        )}
      </Paper>
    </Container>
  );
};

export default Dashboard;
